import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toE164 } from "../lib/phone";
import { initialsOf } from "../lib/profile";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../components/auth/AuthLayout";
import { PhoneField } from "../components/auth/ContactFields";
import {
  colors,
  errorStyle,
  formStyle,
  primaryButtonStyle,
  subtitleStyle,
  titleStyle,
} from "../components/auth/authStyles";

// Shown once after a Google sign-up: Google gives us a name and email but no
// phone number, and the shop needs one to reach customers about orders.
const CompleteProfile = () => {
  const { user, profile, loading, needsProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/account";

  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return <div style={{ minHeight: "100vh" }} />;
  if (!user) return <Navigate to="/login" replace />;
  if (!needsProfile) return <Navigate to={redirectTo} replace />;

  const name = profile?.full_name || user.user_metadata?.full_name || "";
  const firstName = name.split(" ")[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const phoneE164 = toE164(phone);
    if (!phoneE164) {
      setError("Please enter a 10-digit mobile number, like (647) 555-0147.");
      return;
    }
    setSubmitting(true);
    try {
      // contact_pref stays at its "text" default; it's only editable on Account for now.
      await updateProfile({ phone: phoneE164 });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || "Couldn't save your details. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="ALMOST THERE"
      body="Google gave us your name and email. We just need a number so we can tell you the moment your ball lands."
    >
      <form style={formStyle} onSubmit={handleSubmit}>
        <div>
          <h1 style={titleStyle}>
            {firstName ? `One last thing, ${firstName}` : "One last thing"}
          </h1>
          <p style={subtitleStyle}>
            Add your phone so we can reach you about your orders.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.9rem",
            padding: "0.9rem 1rem",
            borderRadius: "12px",
            backgroundColor: "white",
            border: "1px solid #e5e5e5",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: colors.cyan,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "0.95rem",
            }}
          >
            {initialsOf(name, user.email)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            {name && <span style={{ fontWeight: 600 }}>{name}</span>}
            <span
              style={{
                fontSize: "0.875rem",
                color: colors.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user.email}
            </span>
          </div>
        </div>

        {error && (
          <div style={errorStyle} role="alert">
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <PhoneField value={phone} onChange={setPhone} />
        </div>

        <button
          type="submit"
          style={{ ...primaryButtonStyle, opacity: submitting ? 0.7 : 1 }}
          disabled={submitting}
        >
          {submitting ? "Saving..." : "Finish and continue"}
        </button>
      </form>
    </AuthLayout>
  );
};

export default CompleteProfile;
